import type { JourneyKind } from '@manasil/shared';
import { JOURNEY_DURATIONS } from '@manasil/shared';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shell } from '../components/Shell.js';
import {
  Empty,
  Field,
  FieldGroup,
  Loading,
  Modal,
  StatusPill,
  errorMessage,
  useToast,
} from '../components/ui.js';
import { useCreateJourney, useGoals, useJourneys } from '../lib/queries.js';

type Filter = 'all' | 'draft' | 'live';

export function JourneysPage() {
  const [filter, setFilter] = useState<Filter>('all');
  const published = filter === 'all' ? undefined : filter === 'live';
  const { data, isLoading } = useJourneys(published);
  const [open, setOpen] = useState(false);

  return (
    <Shell
      title="Journeys"
      subtitle="Each one is authored whole — days, stories and audio — then published as a unit."
      actions={
        <button type="button" className="btn btn-primary" onClick={() => setOpen(true)}>
          New journey
        </button>
      }
    >
      <div className="row" style={{ gap: 6 }}>
        {(['all', 'draft', 'live'] as Filter[]).map((value) => (
          <button
            key={value}
            type="button"
            className="chip"
            aria-pressed={filter === value}
            onClick={() => setFilter(value)}
          >
            {value === 'all' ? 'All' : value === 'draft' ? 'Drafts' : 'Live'}
          </button>
        ))}
      </div>

      <div className="card">
        {isLoading ? (
          <Loading />
        ) : !data || data.items.length === 0 ? (
          <Empty
            mark="◈"
            title="Nothing here yet"
            hint="Create a journey, add its days, upload the audio, then publish."
          />
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Journey</th>
                  <th>Category</th>
                  <th>Length</th>
                  <th>Days built</th>
                  <th>Access</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((journey) => {
                  const collection = journey.kind === 'COLLECTION';
                  // A collection is "built" when it has anything at all; the publish
                  // checklist says what is still missing, episode by episode.
                  const complete = collection
                    ? journey.episodeCount > 0
                    : journey.dayCount === journey.durationDays;
                  return (
                    <tr key={journey.id}>
                      <td>
                        <Link to={`/journeys/${journey.id}`} className="row-link">
                          {journey.title}
                        </Link>
                        <div className="hint truncate" style={{ maxWidth: 380 }}>
                          {journey.description}
                        </div>
                      </td>
                      <td>
                        <div className="chips">
                          {journey.goalKeys.map((key) => (
                            <span key={key} className="pill pill-draft">
                              {key.replace(/_/g, ' ')}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="nums muted">
                        {collection
                          ? `Collection · ${journey.dailyMinutes} min`
                          : `${journey.durationDays} days · ${journey.dailyMinutes} min`}
                      </td>
                      <td>
                        <span className={`pill ${complete ? 'pill-ok' : 'pill-missing'}`}>
                          {collection
                            ? `${journey.sectionCount} sections · ${journey.episodeCount} episodes`
                            : `${journey.dayCount} / ${journey.durationDays}`}
                        </span>
                      </td>
                      <td className="muted">{journey.isPremium ? 'Premium' : 'Free'}</td>
                      <td>
                        <StatusPill published={journey.published} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {open ? <CreateJourneyModal onClose={() => setOpen(false)} /> : null}
    </Shell>
  );
}

function CreateJourneyModal({ onClose }: { onClose: () => void }) {
  const { data: goals } = useGoals();
  const create = useCreateJourney();
  const { push } = useToast();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // Exactly one category per journey, so this is a single key rather than a list.
  const [categoryKey, setCategoryKey] = useState('');
  const [kind, setKind] = useState<JourneyKind>('DAILY');
  const [durationDays, setDurationDays] = useState<number>(5);
  const [dailyMinutes, setDailyMinutes] = useState<number>(5);
  const [isPremium, setIsPremium] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ready = title.trim().length > 0 && description.trim().length > 0 && categoryKey.length > 0;

  async function submit() {
    setError(null);
    try {
      const journey = await create.mutateAsync({
        title: title.trim(),
        description: description.trim(),
        goalKeys: [categoryKey],
        whatYouGain: [],
        kind,
        // A collection has no fixed length — the API refuses one.
        ...(kind === 'DAILY' ? { durationDays: durationDays as 5 | 7 | 14 | 21 } : {}),
        dailyMinutes: dailyMinutes as 5 | 10 | 15,
        isPremium,
        // Collections are Tamil content first (Thirukkural); daily journeys ship in English.
        language: kind === 'COLLECTION' ? 'ta' : 'en',
      });
      push(`Created “${journey.title}”`);
      navigate(`/journeys/${journey.id}`);
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }

  return (
    <Modal
      title="New journey"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={!ready || create.isPending}
          >
            {create.isPending
              ? 'Creating…'
              : kind === 'COLLECTION'
                ? 'Create and add sections'
                : 'Create and add days'}
          </button>
        </>
      }
    >
      {error ? <div className="banner">{error}</div> : null}

      <FieldGroup
        label="Shape"
        hint={
          kind === 'DAILY'
            ? 'One story a day, earned in order. This is the product.'
            : 'A library: sections → sub-journeys → episodes, played in any order. Thirukkural.'
        }
      >
        <div className="chips">
          <button
            type="button"
            className="chip"
            aria-pressed={kind === 'DAILY'}
            onClick={() => setKind('DAILY')}
          >
            Daily journey
          </button>
          <button
            type="button"
            className="chip"
            aria-pressed={kind === 'COLLECTION'}
            onClick={() => setKind('COLLECTION')}
          >
            Collection
          </button>
        </div>
      </FieldGroup>

      <Field label="Title">
        <input
          className="input"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder={kind === 'COLLECTION' ? 'திருக்குறள்' : 'Calm Your Overthinking Mind'}
        />
      </Field>

      <Field label="Description">
        <textarea
          className="textarea"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="Five short stories to slow down a racing mind."
        />
      </Field>

      <FieldGroup
        label="Category"
        hint="Pick one. This is the only thing that matches this journey to a user."
      >
        <div className="chips">
          {goals
            ?.filter((goal) => goal.active)
            .map((goal) => (
              <button
                key={goal.key}
                type="button"
                className="chip"
                aria-pressed={categoryKey === goal.key}
                onClick={() => setCategoryKey(goal.key)}
              >
                {goal.name.en}
              </button>
            ))}
        </div>
      </FieldGroup>

      <div className="grid-2">
        {kind === 'DAILY' ? (
          <Field label="Length" hint="Fixed set — a journey is 5, 7, 14 or 21 days.">
            <select
              className="select"
              value={durationDays}
              onChange={(event) => setDurationDays(Number(event.target.value))}
            >
              {JOURNEY_DURATIONS.map((days) => (
                <option key={days} value={days}>
                  {days} days
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <Field label="Length" hint="A collection is as long as its episodes.">
            <input className="input" value="No fixed length" disabled readOnly />
          </Field>
        )}

        <Field label="Daily time">
          <select
            className="select"
            value={dailyMinutes}
            onChange={(event) => setDailyMinutes(Number(event.target.value))}
          >
            {[5, 10, 15].map((minutes) => (
              <option key={minutes} value={minutes}>
                {minutes} minutes
              </option>
            ))}
          </select>
        </Field>
      </div>

      <label className="row" style={{ gap: 9, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={isPremium}
          onChange={(event) => setIsPremium(event.target.checked)}
        />
        <span>
          Premium
          <span className="hint" style={{ display: 'block' }}>
            Locked behind a subscription. Leave off for anything onboarding might hand a new user.
          </span>
        </span>
      </label>
    </Modal>
  );
}
